import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useAudioEffects from '@/hooks/useAudioEffects';
import AudioEffectsPanel from './AudioEffectsPanel';
import { useSpeechToText } from '@/hooks/useSpeechToText';
import { useQuery } from 'react-query';
import { speechService, type SupportedLanguage } from '@/services/speechService';
import { logger } from '@/utils/logger';
import { useStabilization, METHOD_INFO, StabilizationMethod } from '@/hooks/useStabilization';
import { StabilityIndicator, AlgorithmSelector, DocumentOverlay, StabilityBadge } from './StabilityIndicator';

interface Point {
  x: number;
  y: number;
}

interface CornerPoints {
  topLeft: Point;
  topRight: Point;
  bottomLeft: Point;
  bottomRight: Point;
}

// Stabilization method options for dropdown
const STABILIZATION_OPTIONS = Object.entries(METHOD_INFO).map(([value, info]) => ({
  value,
  label: info.label,
  description: info.description,
}));

const CameraPage: React.FC = () => {
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isScanMode, setIsScanMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [corners, setCorners] = useState<CornerPoints | null>(null);
  const [draggingCorner, setDraggingCorner] = useState<keyof CornerPoints | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);

  // Stabilization hook (replaces old anti-shake)
  const stabilization = useStabilization({
    method: 'stabilityTimer',
    autoCapture: true,
    showIndicator: true,
    hapticFeedback: true,
  });

  // Legacy states for countdown (still used for some methods)
  const [countdown, setCountdown] = useState<number | null>(null);
  const [captureStatus, setCaptureStatus] = useState<string | null>(null);

  // Microphone states
  const [isMicOn, setIsMicOn] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const editCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Microphone refs
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Audio effects state
  const [showEffectsPanel, setShowEffectsPanel] = useState(false);
  const [processedAudioUrl, setProcessedAudioUrl] = useState<string | null>(null);
  const [isPlayingWithEffects, setIsPlayingWithEffects] = useState(false);

  // Audio effects hook
  const audioEffects = useAudioEffects();

  // Speech-to-text hook
  const speechToText = useSpeechToText({ continuous: true });

  // Fetch supported languages
  const { data: supportedLanguages } = useQuery<SupportedLanguage[]>({
    queryKey: ['speechLanguages'],
    queryFn: () => speechService.getLanguages(),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setCapturedImage(null);
      setCroppedImage(null);
      setIsEditing(false);

      if (!window.isSecureContext) {
        setError('Camera requires a secure connection (HTTPS). Please access this page via HTTPS.');
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera API is not supported in this browser. Please use a modern browser with HTTPS.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsCameraOn(true);
      }
    } catch (err) {
      logger.error('Camera error:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Camera permission denied. Please allow camera access in your browser settings.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found on this device.');
        } else if (err.name === 'NotReadableError') {
          setError('Camera is in use by another application.');
        } else if (err.name === 'OverconstrainedError') {
          setError('Camera does not support the requested settings. Try switching cameras.');
        } else {
          setError(`Failed to access camera: ${err.message}`);
        }
      } else {
        setError('Failed to access camera');
      }
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsScanMode(false);
  }, []);

  const toggleCamera = () => {
    if (isCameraOn) {
      stopCamera();
    } else {
      startCamera();
    }
  };

  const switchCamera = async () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    if (isCameraOn) {
      stopCamera();
      setTimeout(() => {
        startCamera();
      }, 100);
    }
  };

  // Capture single frame from video
  const captureSingleFrame = useCallback((): { imageData: ImageData; dataUrl: string } | null => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);

    return { imageData, dataUrl };
  }, []);

  // Capture using stabilization hook
  const captureWithStabilization = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setCaptureStatus('Capturing with stabilization...');

    const result = await stabilization.captureWithMethod();

    setCaptureStatus(null);

    if (result) {
      setCapturedImage(result.dataUrl);
      setImageSize({
        width: result.imageData.width,
        height: result.imageData.height
      });

      // Initialize corner points
      const padding = 0.1;
      setCorners({
        topLeft: { x: padding, y: padding },
        topRight: { x: 1 - padding, y: padding },
        bottomLeft: { x: padding, y: 1 - padding },
        bottomRight: { x: 1 - padding, y: 1 - padding },
      });

      setIsEditing(true);
      stopCamera();
    }
  }, [stabilization, stopCamera]);

  // Simple capture without anti-shake
  const captureSimple = useCallback(() => {
    const frame = captureSingleFrame();
    if (!frame) return;

    setCapturedImage(frame.dataUrl);
    setImageSize({
      width: frame.imageData.width,
      height: frame.imageData.height
    });

    const padding = 0.1;
    setCorners({
      topLeft: { x: padding, y: padding },
      topRight: { x: 1 - padding, y: padding },
      bottomLeft: { x: padding, y: 1 - padding },
      bottomRight: { x: 1 - padding, y: 1 - padding },
    });

    setIsEditing(true);
    stopCamera();
  }, [captureSingleFrame, stopCamera]);

  // Main capture function - uses stabilization hook
  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const method = stabilization.config.method;

    // If stabilization is off, capture immediately
    if (method === 'off') {
      captureSimple();
      return;
    }

    // For burst mode, no countdown needed - it captures multiple frames
    if (method === 'burstSelect') {
      await captureWithStabilization();
      return;
    }

    // For other methods, check if stable or use countdown
    if (stabilization.state.isStable && stabilization.config.autoCapture) {
      // Already stable, capture now
      await captureWithStabilization();
    } else {
      // Show countdown for manual trigger
      for (let i = 3; i > 0; i--) {
        setCountdown(i);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      setCountdown(null);
      await captureWithStabilization();
    }
  }, [stabilization.config.method, stabilization.state.isStable, stabilization.config.autoCapture, captureSimple, captureWithStabilization]);

  // Handle corner dragging
  const handleMouseDown = (corner: keyof CornerPoints) => (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setDraggingCorner(corner);
  };

  const handleMouseMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!draggingCorner || !containerRef.current || !corners) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();

    let clientX: number, clientY: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Calculate relative position (0-1)
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

    setCorners(prev => prev ? {
      ...prev,
      [draggingCorner]: { x, y }
    } : null);
  }, [draggingCorner, corners]);

  const handleMouseUp = useCallback(() => {
    setDraggingCorner(null);
  }, []);

  useEffect(() => {
    if (draggingCorner) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleMouseMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [draggingCorner, handleMouseMove, handleMouseUp]);

  // Apply perspective transform and crop
  const cropImage = useCallback(() => {
    if (!capturedImage || !corners || !editCanvasRef.current || !imageSize) return;

    const canvas = editCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Convert relative coordinates to actual pixels
      const srcPoints = [
        { x: corners.topLeft.x * img.width, y: corners.topLeft.y * img.height },
        { x: corners.topRight.x * img.width, y: corners.topRight.y * img.height },
        { x: corners.bottomRight.x * img.width, y: corners.bottomRight.y * img.height },
        { x: corners.bottomLeft.x * img.width, y: corners.bottomLeft.y * img.height },
      ];

      // Calculate output dimensions based on the quadrilateral
      const width1 = Math.sqrt(Math.pow(srcPoints[1].x - srcPoints[0].x, 2) + Math.pow(srcPoints[1].y - srcPoints[0].y, 2));
      const width2 = Math.sqrt(Math.pow(srcPoints[2].x - srcPoints[3].x, 2) + Math.pow(srcPoints[2].y - srcPoints[3].y, 2));
      const height1 = Math.sqrt(Math.pow(srcPoints[3].x - srcPoints[0].x, 2) + Math.pow(srcPoints[3].y - srcPoints[0].y, 2));
      const height2 = Math.sqrt(Math.pow(srcPoints[2].x - srcPoints[1].x, 2) + Math.pow(srcPoints[2].y - srcPoints[1].y, 2));

      // Ensure integer dimensions
      const outWidth = Math.round(Math.max(width1, width2));
      const outHeight = Math.round(Math.max(height1, height2));

      // Ensure minimum size
      const finalWidth = Math.max(1, outWidth);
      const finalHeight = Math.max(1, outHeight);

      canvas.width = finalWidth;
      canvas.height = finalHeight;

      // Create temporary canvas for source image
      const srcCanvas = document.createElement('canvas');
      srcCanvas.width = img.width;
      srcCanvas.height = img.height;
      const srcCtx = srcCanvas.getContext('2d');
      if (!srcCtx) return;
      srcCtx.drawImage(img, 0, 0);
      const srcImageData = srcCtx.getImageData(0, 0, img.width, img.height);
      const srcData = srcImageData.data;
      const srcWidth = img.width;
      const srcHeight = img.height;

      // Create output image data
      const dstImageData = ctx.createImageData(finalWidth, finalHeight);
      const dstData = dstImageData.data;

      // Perspective transform using inverse mapping (bilinear interpolation)
      for (let y = 0; y < finalHeight; y++) {
        for (let x = 0; x < finalWidth; x++) {
          // Normalized coordinates (0-1)
          const u = finalWidth > 1 ? x / (finalWidth - 1) : 0;
          const v = finalHeight > 1 ? y / (finalHeight - 1) : 0;

          // Bilinear interpolation to find source coordinates
          const srcX = (1 - u) * (1 - v) * srcPoints[0].x +
                       u * (1 - v) * srcPoints[1].x +
                       u * v * srcPoints[2].x +
                       (1 - u) * v * srcPoints[3].x;
          const srcY = (1 - u) * (1 - v) * srcPoints[0].y +
                       u * (1 - v) * srcPoints[1].y +
                       u * v * srcPoints[2].y +
                       (1 - u) * v * srcPoints[3].y;

          // Get pixel from source using nearest neighbor
          const sx = Math.floor(srcX);
          const sy = Math.floor(srcY);

          if (sx >= 0 && sx < srcWidth && sy >= 0 && sy < srcHeight) {
            const srcIdx = (sy * srcWidth + sx) * 4;
            const dstIdx = (y * finalWidth + x) * 4;
            dstData[dstIdx] = srcData[srcIdx];         // R
            dstData[dstIdx + 1] = srcData[srcIdx + 1]; // G
            dstData[dstIdx + 2] = srcData[srcIdx + 2]; // B
            dstData[dstIdx + 3] = srcData[srcIdx + 3]; // A
          } else {
            // Fill with white for out-of-bounds pixels
            const dstIdx = (y * finalWidth + x) * 4;
            dstData[dstIdx] = 255;     // R
            dstData[dstIdx + 1] = 255; // G
            dstData[dstIdx + 2] = 255; // B
            dstData[dstIdx + 3] = 255; // A
          }
        }
      }

      ctx.putImageData(dstImageData, 0, 0);

      // Get cropped image
      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.95);
      setCroppedImage(croppedDataUrl);
      setIsEditing(false);
    };
    img.src = capturedImage;
  }, [capturedImage, corners, imageSize]);

  // Download cropped image
  const downloadImage = useCallback(() => {
    if (!croppedImage) return;

    const link = document.createElement('a');
    link.href = croppedImage;
    link.download = `scanned-document-${Date.now()}.jpg`;
    link.click();
  }, [croppedImage]);

  // Retake photo
  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setCroppedImage(null);
    setIsEditing(false);
    setCorners(null);
    startCamera();
  }, [startCamera]);

  // Microphone functions
  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average volume level
    const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
    const normalizedLevel = Math.min(100, (average / 128) * 100);
    setAudioLevel(normalizedLevel);

    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  }, []);

  const startMicrophone = useCallback(async () => {
    try {
      setMicError(null);

      if (!window.isSecureContext) {
        setMicError('Microphone requires a secure connection (HTTPS).');
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicError('Microphone API is not supported in this browser.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      micStreamRef.current = stream;

      // Create audio context and analyser
      const audioContext = new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      setIsMicOn(true);

      // Start audio level monitoring
      updateAudioLevel();
    } catch (err) {
      logger.error('Microphone error:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setMicError('Microphone permission denied. Please allow microphone access.');
        } else if (err.name === 'NotFoundError') {
          setMicError('No microphone found on this device.');
        } else if (err.name === 'NotReadableError') {
          setMicError('Microphone is in use by another application.');
        } else {
          setMicError(`Failed to access microphone: ${err.message}`);
        }
      } else {
        setMicError('Failed to access microphone');
      }
    }
  }, [updateAudioLevel]);

  const stopMicrophone = useCallback(() => {
    // Stop any ongoing recording first
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);

    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop microphone stream
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }

    analyserRef.current = null;
    setIsMicOn(false);
    setAudioLevel(0);
  }, []);

  const toggleMicrophone = () => {
    if (isMicOn) {
      stopMicrophone();
    } else {
      startMicrophone();
    }
  };

  // Recording functions
  const startRecording = useCallback(() => {
    if (!micStreamRef.current) return;

    try {
      audioChunksRef.current = [];

      // Try different mimeTypes in order of preference
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/mpeg',
        '', // Empty string = browser default
      ];

      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (mimeType === '' || MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      const recorderOptions: MediaRecorderOptions = {};
      if (selectedMimeType) {
        recorderOptions.mimeType = selectedMimeType;
      }

      logger.log('Using MediaRecorder mimeType:', selectedMimeType || 'browser default');
      const mediaRecorder = new MediaRecorder(micStreamRef.current, recorderOptions);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Use the actual mimeType from the recorder
        const actualMimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        setRecordedAudio(audioBlob);

        // Clean up old URL
        if (recordedAudioUrl) {
          URL.revokeObjectURL(recordedAudioUrl);
        }

        const url = URL.createObjectURL(audioBlob);
        setRecordedAudioUrl(url);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingDuration(0);

      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      logger.error('Recording error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setMicError(`Failed to start recording: ${errorMessage}`);
    }
  }, [recordedAudioUrl]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setIsRecording(false);
  }, []);

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const playRecording = useCallback(() => {
    if (!recordedAudioUrl) return;

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }

    const audio = new Audio(recordedAudioUrl);
    audio.onended = () => {
      setIsPlaying(false);
      audioPlayerRef.current = null;
    };
    audio.onpause = () => {
      setIsPlaying(false);
    };
    audio.play();
    audioPlayerRef.current = audio;
    setIsPlaying(true);
  }, [recordedAudioUrl]);

  const stopPlayback = useCallback(() => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
      audioPlayerRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const deleteRecording = useCallback(() => {
    // Stop playback if playing
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }

    // Clean up URL
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
    }

    setRecordedAudio(null);
    setRecordedAudioUrl(null);
    setIsPlaying(false);
    setRecordingDuration(0);
  }, [recordedAudioUrl]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Audio effects handlers
  const handlePlayWithEffects = useCallback(async () => {
    if (!recordedAudio) return;
    setIsPlayingWithEffects(true);
    await audioEffects.playWithEffects(recordedAudio);
  }, [recordedAudio, audioEffects]);

  const handleStopEffectsPlayback = useCallback(() => {
    audioEffects.stopPlayback();
    setIsPlayingWithEffects(false);
  }, [audioEffects]);

  const handleProcessAudio = useCallback(async () => {
    if (!recordedAudio) return;
    try {
      const processedBlob = await audioEffects.processAudio(recordedAudio);

      // Clean up old URL
      if (processedAudioUrl) {
        URL.revokeObjectURL(processedAudioUrl);
      }

      const url = URL.createObjectURL(processedBlob);
      setProcessedAudioUrl(url);

      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `processed-audio-${Date.now()}.wav`;
      link.click();
    } catch (err) {
      logger.error('Failed to process audio:', err);
    }
  }, [recordedAudio, processedAudioUrl, audioEffects]);

  // Start/stop stabilization analysis when camera is on
  useEffect(() => {
    if (isCameraOn && videoRef.current && canvasRef.current && !capturedImage) {
      stabilization.startAnalysis(videoRef.current, canvasRef.current);
    } else {
      stabilization.stopAnalysis();
    }
    return () => {
      stabilization.stopAnalysis();
    };
  }, [isCameraOn, capturedImage, stabilization]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      // Cleanup microphone
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      // Cleanup recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }
      // Cleanup processed audio URL
      if (processedAudioUrl) {
        URL.revokeObjectURL(processedAudioUrl);
      }
      // Stop effects playback
      audioEffects.stopPlayback();
      // Stop stabilization
      stabilization.stopAnalysis();
    };
  }, [processedAudioUrl, audioEffects, stabilization]);

  // Render corner handle
  const renderCornerHandle = (corner: keyof CornerPoints, position: Point) => (
    <div
      key={corner}
      className="absolute w-8 h-8 -ml-4 -mt-4 cursor-move touch-none"
      style={{ left: `${position.x * 100}%`, top: `${position.y * 100}%` }}
      onMouseDown={handleMouseDown(corner)}
      onTouchStart={handleMouseDown(corner)}
    >
      <div className="w-full h-full bg-blue-500 rounded-full border-4 border-white shadow-lg" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/app"
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Document Scanner</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Scan documents with edge detection</p>
          </div>
        </div>
      </div>

      {/* Hidden canvases for image processing */}
      <canvas ref={canvasRef} className="hidden" />
      <canvas ref={editCanvasRef} className="hidden" />

      {/* Camera/Image View */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 overflow-hidden">
        <div
          ref={containerRef}
          className="relative aspect-video bg-gray-900 flex items-center justify-center select-none"
        >
          {/* Live camera view */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${isCameraOn && !capturedImage ? '' : 'hidden'}`}
          />

          {/* Captured image for editing */}
          {isEditing && capturedImage && (
            <>
              <img
                src={capturedImage}
                alt="Captured"
                className="w-full h-full object-contain"
                draggable={false}
              />
              {/* Corner handles and edge lines */}
              {corners && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <polygon
                    points={`${corners.topLeft.x * 100},${corners.topLeft.y * 100} ${corners.topRight.x * 100},${corners.topRight.y * 100} ${corners.bottomRight.x * 100},${corners.bottomRight.y * 100} ${corners.bottomLeft.x * 100},${corners.bottomLeft.y * 100}`}
                    fill="rgba(59, 130, 246, 0.2)"
                    stroke="#3B82F6"
                    strokeWidth="0.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}
              {corners && (
                <>
                  {renderCornerHandle('topLeft', corners.topLeft)}
                  {renderCornerHandle('topRight', corners.topRight)}
                  {renderCornerHandle('bottomLeft', corners.bottomLeft)}
                  {renderCornerHandle('bottomRight', corners.bottomRight)}
                </>
              )}
            </>
          )}

          {/* Cropped result */}
          {croppedImage && !isEditing && (
            <img
              src={croppedImage}
              alt="Cropped"
              className="w-full h-full object-contain"
            />
          )}

          {/* Camera off placeholder */}
          {!isCameraOn && !capturedImage && !croppedImage && (
            <div className="absolute inset-0 flex items-center justify-center text-center text-gray-400 dark:text-gray-500">
              <div>
                <svg className="w-24 h-24 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg">Ready to Scan</p>
                <p className="text-sm mt-1">Turn on camera to scan a document</p>
              </div>
            </div>
          )}

          {/* Camera status indicator */}
          {isCameraOn && !capturedImage && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 px-3 py-1.5 rounded-full">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-white text-sm font-medium">LIVE</span>
            </div>
          )}

          {/* Scan mode indicator */}
          {isCameraOn && isScanMode && !capturedImage && (
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-blue-500/80 px-3 py-1.5 rounded-full">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-white text-sm font-medium">SCAN MODE</span>
            </div>
          )}

          {/* Scan mode overlay with document frame */}
          {isCameraOn && isScanMode && !capturedImage && stabilization.config.method !== 'edgeMotion' && (
            <div className="absolute inset-0 pointer-events-none">
              {/* Document frame guide */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-[85%] h-[85%] border-2 border-dashed border-blue-400/70 rounded-lg">
                  {/* Corner brackets */}
                  <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-lg" />
                  <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-lg" />
                  <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-lg" />
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-lg" />
                </div>
              </div>

              {/* Instruction text */}
              <div className="absolute bottom-8 left-0 right-0 text-center">
                <p className="text-white text-sm font-medium bg-black/50 inline-block px-4 py-2 rounded-full">
                  Align document within the frame, then capture
                </p>
              </div>
            </div>
          )}

          {/* Edge Detection Overlay (Google-style) */}
          {isCameraOn && !capturedImage && stabilization.config.method === 'edgeMotion' && containerRef.current && videoRef.current && (
            <>
              <DocumentOverlay
                corners={stabilization.state.detectedCorners}
                isStable={stabilization.state.isStable}
                containerWidth={containerRef.current.offsetWidth}
                containerHeight={containerRef.current.offsetHeight}
                videoWidth={320}
                videoHeight={240}
              />
              <div className="absolute bottom-8 left-0 right-0 text-center">
                <p className="text-white text-sm font-medium bg-black/50 inline-block px-4 py-2 rounded-full">
                  {stabilization.state.statusMessage || 'Position document in frame'}
                </p>
              </div>
            </>
          )}

          {/* Stability Indicator Ring (for applicable methods) */}
          {isCameraOn && !capturedImage && stabilization.config.method !== 'off' && stabilization.config.method !== 'burstSelect' && stabilization.config.method !== 'edgeMotion' && (
            <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2">
              <StabilityIndicator
                stabilityScore={stabilization.state.stabilityScore}
                isStable={stabilization.state.isStable}
                statusMessage={stabilization.state.statusMessage}
                mode="ring"
                size="md"
                showPercentage={true}
              />
            </div>
          )}

          {/* Editing mode indicator */}
          {isEditing && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-blue-500/80 px-3 py-1.5 rounded-full">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="text-white text-sm font-medium">ADJUST CORNERS</span>
            </div>
          )}

          {/* Countdown overlay */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <div className="text-center">
                <div className="text-9xl font-bold text-white animate-pulse drop-shadow-lg">
                  {countdown}
                </div>
                <p className="text-white text-xl mt-4 font-medium">Hold steady...</p>
              </div>
            </div>
          )}

          {/* Capture status overlay */}
          {captureStatus && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mb-4" />
                <p className="text-white text-lg font-medium">{captureStatus}</p>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {/* Camera controls - shown when no image captured */}
            {!capturedImage && !croppedImage && (
              <>
                <button
                  onClick={toggleCamera}
                  className={`flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg ${
                    isCameraOn
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  {isCameraOn ? (
                    <>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                      Turn Off
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Turn On Camera
                    </>
                  )}
                </button>

                {isCameraOn && (
                  <>
                    <button
                      onClick={switchCamera}
                      className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Switch Camera
                    </button>

                    <button
                      onClick={() => setIsScanMode(!isScanMode)}
                      className={`flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-medium transition-colors ${
                        isScanMode
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Scan Mode {isScanMode ? 'ON' : 'OFF'}
                    </button>

                    {/* Stabilization Method Dropdown */}
                    <div className="w-full sm:w-auto">
                      <AlgorithmSelector
                        value={stabilization.config.method}
                        onChange={(value) => stabilization.setMethod(value as StabilizationMethod)}
                        options={STABILIZATION_OPTIONS}
                        disabled={countdown !== null || captureStatus !== null}
                        className="min-w-[200px]"
                      />
                    </div>

                    {/* Stability Indicator */}
                    {stabilization.config.method !== 'off' && stabilization.config.method !== 'burstSelect' && (
                      <StabilityBadge
                        stabilityScore={stabilization.state.stabilityScore}
                        isStable={stabilization.state.isStable}
                      />
                    )}

                    <button
                      onClick={capturePhoto}
                      disabled={countdown !== null || captureStatus !== null || stabilization.state.isAnalyzing}
                      className={`flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold transition-colors shadow-lg ${
                        countdown !== null || captureStatus !== null || stabilization.state.isAnalyzing
                          ? 'bg-gray-400 cursor-not-allowed'
                          : stabilization.state.isStable || stabilization.config.method === 'off' || stabilization.config.method === 'burstSelect'
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                      }`}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {stabilization.state.isStable ? 'Capture Now' :
                       stabilization.config.method === 'off' ? 'Capture' :
                       stabilization.config.method === 'burstSelect' ? 'Burst Capture' : 'Capture'}
                    </button>
                  </>
                )}
              </>
            )}

            {/* Editing controls */}
            {isEditing && (
              <>
                <button
                  onClick={retakePhoto}
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retake
                </button>

                <button
                  onClick={cropImage}
                  className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold bg-green-500 hover:bg-green-600 text-white transition-colors shadow-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Crop Document
                </button>
              </>
            )}

            {/* Result controls */}
            {croppedImage && !isEditing && (
              <>
                <button
                  onClick={retakePhoto}
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Scan Another
                </button>

                <button
                  onClick={downloadImage}
                  className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold bg-blue-500 hover:bg-blue-600 text-white transition-colors shadow-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </>
            )}
          </div>

          {/* Status info */}
          <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400 space-y-1">
            {isCameraOn && !capturedImage && (
              <>
                <p>Camera: <span className="font-medium">{facingMode === 'user' ? 'Front' : 'Back'}</span></p>
                {stabilization.config.method !== 'off' && (
                  <p className="text-purple-600 dark:text-purple-400">
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      {METHOD_INFO[stabilization.config.method].label}: {METHOD_INFO[stabilization.config.method].description}
                    </span>
                  </p>
                )}
              </>
            )}
            {isEditing && (
              <p className="text-blue-600 dark:text-blue-400 font-medium">Drag the corner points to adjust document edges</p>
            )}
            {croppedImage && !isEditing && (
              <p className="text-green-600 dark:text-green-400 font-medium">Document scanned successfully!</p>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
        <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">How to Scan Documents</h3>
        <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
          <li>1. Select a stabilization method below, then turn on the camera</li>
          <li>2. Position your document within the frame</li>
          <li>3. Wait for the stability indicator to turn green, or click Capture</li>
          <li>4. Drag the blue corner points to match document edges</li>
          <li>5. Click "Crop Document" to extract and straighten the document</li>
          <li>6. Download the scanned document</li>
        </ul>

        {/* Stabilization Methods - Selectable */}
        <div className="mt-3 space-y-2">
          <p className="text-xs font-semibold text-blue-800 dark:text-blue-300">Stabilization Methods (click to select):</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            {(Object.entries(METHOD_INFO) as [StabilizationMethod, { label: string; description: string }][])
              .filter(([key]) => key !== 'off')
              .map(([key, info]) => {
                const isSelected = stabilization.config.method === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => stabilization.setMethod(key)}
                    className={`p-2 rounded text-left transition-colors ${
                      isSelected
                        ? 'bg-purple-100 dark:bg-purple-900/40 ring-2 ring-purple-500 dark:ring-purple-400'
                        : 'bg-white/50 dark:bg-gray-800/50 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                    } ${key === 'edgeMotion' ? 'sm:col-span-2' : ''}`}
                  >
                    <strong className={isSelected ? 'text-purple-700 dark:text-purple-300' : 'text-purple-600 dark:text-purple-400'}>
                      {info.label}
                    </strong>
                    <span className="text-gray-600 dark:text-gray-400"> {info.description}</span>
                  </button>
                );
              })}
          </div>
        </div>

        <p className="text-xs text-blue-600 dark:text-blue-500 mt-3">
          Tip: For best results, ensure good lighting and place the document on a contrasting background.
        </p>
      </div>

      {/* Microphone Test Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Microphone Test</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Test your microphone input</p>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isMicOn ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
              <div className={`w-3 h-3 rounded-full ${isMicOn ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <span className={`text-sm font-medium ${isMicOn ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                {isMicOn ? 'Listening' : 'Off'}
              </span>
            </div>
          </div>

          {/* Audio Level Visualization */}
          <div className="mb-6">
            <div className="flex items-center gap-4">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              <div className="flex-1">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-75 rounded-full"
                    style={{
                      width: `${audioLevel}%`,
                      background: audioLevel > 70
                        ? 'linear-gradient(90deg, #22c55e, #eab308, #ef4444)'
                        : audioLevel > 40
                        ? 'linear-gradient(90deg, #22c55e, #eab308)'
                        : '#22c55e',
                    }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>Quiet</span>
                  <span>Normal</span>
                  <span>Loud</span>
                </div>
              </div>
              <div className="w-16 text-right">
                <span className={`text-2xl font-bold ${isMicOn ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                  {Math.round(audioLevel)}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">%</span>
              </div>
            </div>
          </div>

          {/* Level Bars Visualization */}
          {isMicOn && (
            <div className="flex items-end justify-center gap-1 h-16 mb-6">
              {[...Array(20)].map((_, i) => {
                const barHeight = Math.max(4, Math.min(64, (audioLevel / 100) * 64 * (0.5 + Math.random() * 0.5)));
                const isActive = (i / 20) * 100 < audioLevel;
                return (
                  <div
                    key={i}
                    className={`w-2 rounded-full transition-all duration-75 ${
                      isActive
                        ? i > 15 ? 'bg-red-500' : i > 10 ? 'bg-yellow-500' : 'bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    style={{ height: `${isActive ? barHeight : 4}px` }}
                  />
                );
              })}
            </div>
          )}

          {/* Microphone Error */}
          {micError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">{micError}</span>
              </div>
            </div>
          )}

          {/* Microphone and Recording Buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            {/* Microphone Toggle Button */}
            <button
              onClick={toggleMicrophone}
              disabled={isRecording}
              className={`flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg ${
                isRecording
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isMicOn
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              {isMicOn ? (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                  Turn Off
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  Turn On
                </>
              )}
            </button>

            {/* Recording Button */}
            <button
              onClick={toggleRecording}
              disabled={!isMicOn}
              className={`flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg ${
                !isMicOn
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400'
                  : isRecording
                  ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
                  : 'bg-rose-500 hover:bg-rose-600 text-white'
              }`}
            >
              {isRecording ? (
                <>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <div className="w-4 h-4 bg-white rounded-sm" />
                  </div>
                  Stop ({formatDuration(recordingDuration)})
                </>
              ) : (
                <>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </div>
                  Record
                </>
              )}
            </button>
          </div>

          {/* Recorded Audio Playback */}
          {recordedAudio && !isRecording && (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Recorded Audio ({formatDuration(recordingDuration)})
                  </span>
                </div>
                <button
                  onClick={deleteRecording}
                  className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Delete recording"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {/* Audio waveform placeholder */}
              <div className="flex items-center gap-1 h-12 mb-3 px-2 bg-gray-100 dark:bg-gray-600 rounded-lg">
                {[...Array(40)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-1 rounded-full transition-all duration-150 ${
                      isPlaying ? 'bg-rose-500' : 'bg-gray-400 dark:bg-gray-500'
                    }`}
                    style={{
                      height: `${Math.max(8, Math.sin(i * 0.5) * 20 + Math.random() * 20 + 12)}px`,
                    }}
                  />
                ))}
              </div>

              {/* Playback controls */}
              <div className="flex justify-center gap-3">
                <button
                  onClick={isPlaying ? stopPlayback : playRecording}
                  className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow ${
                    isPlaying
                      ? 'bg-gray-600 hover:bg-gray-700 text-white'
                      : 'bg-rose-500 hover:bg-rose-600 text-white'
                  }`}
                >
                  {isPlaying ? (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <rect x="6" y="4" width="4" height="16" rx="1" />
                        <rect x="14" y="4" width="4" height="16" rx="1" />
                      </svg>
                      Stop
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      Play
                    </>
                  )}
                </button>

                {/* Audio Effects Toggle Button */}
                <button
                  onClick={() => setShowEffectsPanel(!showEffectsPanel)}
                  className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow ${
                    showEffectsPanel
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-purple-500 hover:bg-purple-600 text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  Effects
                </button>
              </div>

              {/* Audio Effects Panel */}
              {showEffectsPanel && (
                <div className="mt-4">
                  <AudioEffectsPanel
                    state={audioEffects.state}
                    isProcessing={audioEffects.isProcessing}
                    error={audioEffects.error}
                    onUpdateEffect={audioEffects.updateEffect}
                    onToggleEffect={audioEffects.toggleEffect}
                    onResetEffects={audioEffects.resetEffects}
                    onPlayWithEffects={handlePlayWithEffects}
                    onProcessAudio={handleProcessAudio}
                    onStopPlayback={handleStopEffectsPlayback}
                    hasRecording={!!recordedAudio}
                    isPlaying={isPlayingWithEffects}
                  />
                </div>
              )}
            </div>
          )}

          {/* Microphone Instructions */}
          <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {isRecording ? (
              <p className="text-rose-600 dark:text-rose-400 font-medium">Recording in progress... Click Stop to finish</p>
            ) : isMicOn ? (
              <p>Speak into your microphone to test audio levels, or click Record to capture audio</p>
            ) : (
              <p>Click "Turn On" to test your microphone</p>
            )}
          </div>
        </div>
      </div>

      {/* Voice-to-Text Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Voice-to-Text</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Convert speech to text using Azure Speech Services</p>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              speechToText.isListening
                ? 'bg-green-100 dark:bg-green-900/30'
                : speechToText.isConnecting
                ? 'bg-yellow-100 dark:bg-yellow-900/30'
                : 'bg-gray-100 dark:bg-gray-700'
            }`}>
              <div className={`w-3 h-3 rounded-full ${
                speechToText.isListening
                  ? 'bg-green-500 animate-pulse'
                  : speechToText.isConnecting
                  ? 'bg-yellow-500 animate-pulse'
                  : 'bg-gray-400'
              }`} />
              <span className={`text-sm font-medium ${
                speechToText.isListening
                  ? 'text-green-700 dark:text-green-400'
                  : speechToText.isConnecting
                  ? 'text-yellow-700 dark:text-yellow-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {speechToText.isListening ? 'Listening' : speechToText.isConnecting ? 'Connecting...' : 'Off'}
              </span>
            </div>
          </div>

          {/* Language Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Language
            </label>
            <select
              value={speechToText.currentLanguage}
              onChange={(e) => speechToText.setLanguage(e.target.value)}
              disabled={speechToText.isListening || speechToText.isConnecting}
              className="w-full md:w-64 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {supportedLanguages?.map((lang: SupportedLanguage) => (
                <option key={lang.code} value={lang.code}>
                  {lang.native} ({lang.name})
                </option>
              )) || (
                <>
                  <option value="en-US">English (US)</option>
                  <option value="zh-TW">繁體中文 (Traditional Chinese)</option>
                  <option value="zh-CN">简体中文 (Simplified Chinese)</option>
                  <option value="ja-JP">日本語 (Japanese)</option>
                </>
              )}
            </select>
          </div>

          {/* Transcript Display */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Transcript
            </label>
            <div className="relative">
              <div className="min-h-[120px] max-h-[300px] overflow-y-auto p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900">
                {speechToText.transcript || speechToText.interimTranscript ? (
                  <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                    {speechToText.transcript}
                    {speechToText.interimTranscript && (
                      <span className="text-gray-400 dark:text-gray-500 italic">
                        {speechToText.transcript ? ' ' : ''}{speechToText.interimTranscript}
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-gray-400 dark:text-gray-500 italic">
                    {speechToText.isListening
                      ? 'Listening... Start speaking'
                      : 'Click "Start Listening" and speak to see transcript here'}
                  </p>
                )}
              </div>

              {/* Word count */}
              {speechToText.transcript && (
                <div className="absolute bottom-2 right-2 text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 px-2 py-0.5 rounded">
                  {speechToText.transcript.split(/\s+/).filter(Boolean).length} words
                </div>
              )}
            </div>
          </div>

          {/* Speech-to-Text Error */}
          {speechToText.error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">{speechToText.error}</span>
              </div>
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            {/* Start/Stop Listening Button */}
            <button
              onClick={speechToText.isListening ? speechToText.stopListening : speechToText.startListening}
              disabled={speechToText.isConnecting}
              className={`flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg ${
                speechToText.isConnecting
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : speechToText.isListening
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {speechToText.isConnecting ? (
                <>
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Connecting...
                </>
              ) : speechToText.isListening ? (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                  Stop Listening
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  Start Listening
                </>
              )}
            </button>

            {/* Copy Button */}
            <button
              onClick={() => {
                if (speechToText.transcript) {
                  navigator.clipboard.writeText(speechToText.transcript);
                }
              }}
              disabled={!speechToText.transcript}
              className={`flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-medium transition-all duration-200 shadow ${
                !speechToText.transcript
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400'
                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </button>

            {/* Clear Button */}
            <button
              onClick={speechToText.resetTranscript}
              disabled={!speechToText.transcript && !speechToText.interimTranscript}
              className={`flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-medium transition-all duration-200 shadow ${
                !speechToText.transcript && !speechToText.interimTranscript
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400'
                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear
            </button>
          </div>

          {/* Instructions */}
          <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {speechToText.isListening ? (
              <p className="text-green-600 dark:text-green-400 font-medium">
                Listening... Speak clearly into your microphone
              </p>
            ) : speechToText.isConnecting ? (
              <p className="text-yellow-600 dark:text-yellow-400 font-medium">
                Connecting to Azure Speech Services...
              </p>
            ) : (
              <p>Select a language and click "Start Listening" to begin transcription</p>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-xs text-blue-700 dark:text-blue-400">
                <p className="font-medium mb-1">Azure Cognitive Services Speech-to-Text</p>
                <p>This feature uses Azure Speech Services for real-time speech recognition. Transcription happens in the cloud with low latency. Interim results are shown in gray as you speak.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraPage;
